import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Loader2, DollarSign, TrendingUp, Truck, Search, CheckCircle } from 'lucide-react';
import { dispatchAPI } from '../../services/api';
import { toast } from 'sonner';
import Pagination from '../ui/Pagination';

const fmt = (v) => {
  if (!v && v !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(v);
};

const ExtraChargesTracker = ({ onBack }) => {
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeTab, setActiveTab] = useState('dispatches'); // 'dispatches' | 'report'
  const [transporterReport, setTransporterReport] = useState([]);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ payment_date: '', payment_mode: '', notes: '' });

  const fetchDispatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: 50 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const data = await dispatchAPI.getExtraChargesDispatchSummary(params);
      setDispatches(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      setDispatches([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  const fetchReport = useCallback(async () => {
    try {
      const data = await dispatchAPI.getTransporterReport();
      setTransporterReport(data || []);
    } catch {
      setTransporterReport([]);
    }
  }, []);

  useEffect(() => { fetchDispatches(); }, [fetchDispatches]);
  useEffect(() => { if (activeTab === 'report') fetchReport(); }, [activeTab, fetchReport]);

  const handleMarkPaid = async () => {
    if (!payModal) return;
    try {
      await dispatchAPI.markExtraChargesPaid(payModal.id, payForm);
      toast.success('Extra charges marked as paid');
      setPayModal(null);
      setPayForm({ payment_date: '', payment_mode: '', notes: '' });
      fetchDispatches();
    } catch { toast.error('Failed to record payment'); }
  };

  return (
    <div className="p-5 lg:p-6" data-testid="extra-charges-tracker">
      <button onClick={onBack} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Dispatches
      </button>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>All Dispatches — Extra Charges</h1>
          <p className="text-xs text-gray-500 mt-0.5">Track additional logistics charges (unloading, demurrage, etc.) per dispatch</p>
        </div>
        <div className="flex bg-gray-100 p-0.5 rounded-lg">
          <button onClick={() => setActiveTab('dispatches')} data-testid="tab-dispatches"
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-all ${activeTab === 'dispatches' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
            <DollarSign className="w-3 h-3 inline mr-1" />All Dispatches
          </button>
          <button onClick={() => setActiveTab('report')} data-testid="tab-report"
            className={`px-3 py-1.5 text-[11px] font-semibold rounded-md transition-all ${activeTab === 'report' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
            <TrendingUp className="w-3 h-3 inline mr-1" />Transporter Report
          </button>
        </div>
      </div>

      {activeTab === 'dispatches' && (
        <>
          {/* Search + Filter */}
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input data-testid="ec-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search DSP#, OC#, customer..."
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
            </div>
            <select data-testid="ec-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 text-xs border border-gray-200 rounded-lg">
              <option value="">All</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          {/* Dispatch Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <table className="w-full text-xs" data-testid="ec-dispatch-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">DSP #</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">OC #</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Customer</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Transporter</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Vehicle</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Total Extra Charge</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {dispatches.map((d, idx) => (
                    <tr key={d.id || idx} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`ec-row-${idx}`}>
                      <td className="px-4 py-3 font-mono font-semibold text-[#24549c]">{d.dispatch_number}</td>
                      <td className="px-4 py-3 text-gray-600">{d.oc_number || '-'}</td>
                      <td className="px-4 py-3 text-gray-700">{d.customer_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{d.transporter_name || '-'}</td>
                      <td className="px-4 py-3 text-gray-500 font-mono">{d.vehicle_no || '-'}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">₹{fmt(d.total_extra_charges)}</td>
                      <td className="px-4 py-3 text-center">
                        {d.payment_status === 'Paid'
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 rounded-full"><CheckCircle className="w-2.5 h-2.5" />Paid</span>
                          : <span className="px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 rounded-full">Pending</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-center">
                        {d.payment_status !== 'Paid' && (
                          <button onClick={() => { setPayModal(d); setPayForm({ payment_date: new Date().toISOString().slice(0, 10), payment_mode: '', notes: '' }); }}
                            className="px-2.5 py-1 text-[10px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg" data-testid={`ec-mark-paid-${d.id}`}>
                            Mark Paid
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {dispatches.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                      <DollarSign className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                      No dispatches with extra charges found
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          {total > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={50} onPageChange={setPage} />}
        </>
      )}

      {activeTab === 'report' && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Transporter Cost Report</h3>
            <p className="text-[10px] text-gray-500 mt-0.5">Freight charges vs Extra charges per transporter</p>
          </div>
          {transporterReport.length === 0 ? (
            <div className="p-12 text-center">
              <Truck className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">No transporter data available</p>
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="transporter-report-table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Transporter</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Dispatches</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-blue-600 uppercase">Total Freight</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-red-600 uppercase">Total Extra Charges</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-900 uppercase">Total Logistics Cost</th>
                </tr>
              </thead>
              <tbody>
                {transporterReport.map((t, idx) => (
                  <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`transporter-row-${idx}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{t.transporter}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{t.dispatch_count}</td>
                    <td className="px-4 py-3 text-right font-semibold text-blue-700">INR {t.total_freight.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-semibold text-red-600">INR {t.total_extra_charges.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-bold text-gray-900">INR {t.total_logistics_cost.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 font-bold">
                  <td className="px-4 py-3 text-gray-700">TOTAL</td>
                  <td className="px-4 py-3 text-center">{transporterReport.reduce((a, t) => a + t.dispatch_count, 0)}</td>
                  <td className="px-4 py-3 text-right text-blue-700">INR {transporterReport.reduce((a, t) => a + t.total_freight, 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-red-600">INR {transporterReport.reduce((a, t) => a + t.total_extra_charges, 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-900">INR {transporterReport.reduce((a, t) => a + t.total_logistics_cost, 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Mark Paid Modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="ec-pay-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Mark Extra Charges Paid — {payModal.dispatch_number}</h3>
            <div className="text-xs text-gray-500 mb-4">
              Total Extra Charges: <span className="font-bold text-red-600">₹{fmt(payModal.total_extra_charges)}</span>
              <span className="mx-2">|</span>Charges: {payModal.charge_count} items
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Payment Date</label>
                <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="ec-pay-date" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Payment Mode</label>
                <select value={payForm.payment_mode} onChange={e => setPayForm(f => ({ ...f, payment_mode: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" data-testid="ec-pay-mode">
                  <option value="">Select</option>
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cheque">Cheque</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-0.5">Notes</label>
                <input value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" placeholder="Optional notes" data-testid="ec-pay-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPayModal(null)} className="px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleMarkPaid} className="px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-lg" data-testid="ec-pay-confirm">Mark Paid</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtraChargesTracker;
